import type { PropDef, PropKind, TrackDef, Vec2 } from '../core/types';
import { hash01 } from '../core/math';
import { roundedLoop } from './track-geometry';

/**
 * Hungaroring – a valódi vonalvezetés ritmusát követő, simított változat.
 * Hosszú célegyenes, jobbos T1, felkapaszkodó jobb oldal, lassú belső szakasz,
 * majd az utolsó kanyarok visszavezetnek a rajtvonalra.
 *
 * A vonalvezetés a Wikimedia Commons Hungaroring.svg (Will Pittenger,
 * CC BY-SA 3.0) alapján, a játékhoz egyszerűsítve.
 */
const CENTERLINE: Vec2[] = roundedLoop([
  { x: 1098, y: 3109, r: 180 },
  { x: 45, y: 3030, r: 180 },
  { x: 45, y: 2474, r: 180 },
  { x: 915, y: 2060, r: 180 },
  { x: 620, y: 394, r: 180 },
  { x: 40, y: -111, r: 180 },
  { x: 253, y: -396, r: 180 },
  { x: 1074, y: -319, r: 180 },
  { x: 1608, y: 180, r: 180 },
  { x: 2111, y: 22, r: 180 },
  { x: 2468, y: 595, r: 180 },
  { x: 3125, y: 935, r: 180 },
  { x: 3059, y: 2358, r: 180 },
  { x: 2171, y: 2454, r: 180 },
]);
const props: PropDef[] = [];
const add = (kind: PropKind, s: number, lateral: number, scale = 1, variant = 0): void => {
  props.push({ kind, s, lateral, scale, variant });
};

// --- depó a célegyenes belső oldalán (a boxutca-festés a statikus rétegben készül) ---
// boxutca-festés a statikus rétegben készül (theme circuit)

// --- lelátók a külső oldalon ---
add('grandstand', 0.035, 252, 1.7, 0);
add('grandstand', 0.085, 252, 1.7, 1);
add('grandstand', 0.135, 252, 1.5, 2);
add('grandstand', 0.245, 268, 1.4, 0);
add('grandstand', 0.66, 262, 1.3, 1);

// --- gumifalak a kanyarok külső ívén ---
for (const s of [0.2, 0.27, 0.34, 0.41, 0.48, 0.56, 0.63, 0.71, 0.79, 0.87, 0.94]) {
  add('tyreWall', s, 186, 1, Math.floor(hash01(s * 991) * 3));
}

// --- zászlórudak, reklámtáblák, marsallállások ---
for (let i = 0; i < 22; i++) {
  const s = (i + 0.3) / 22;
  add('flagpole', s, i % 2 === 0 ? 206 : -196, 1, i % 4);
}
for (let i = 0; i < 16; i++) {
  const s = (i + 0.4) / 16;
  add('sign', s, i % 2 === 0 ? 176 : -176, 1, i % 4);
}
for (let i = 0; i < 26; i++) add('lamp', i / 26, i % 2 === 0 ? 178 : -178, 1, i % 3);

// --- fű, bokrok a pályán kívül ---
for (let i = 0; i < 30; i++) {
  const s = (i + 0.2) / 30;
  const h = hash01(i * 92821 + 7);
  if (h > 0.45) add('tree', s, 320 + h * 80, 0.8 + h * 0.6, Math.floor(h * 3));
}

// --- pályaelemek ---
for (const s of [0.06, 0.22, 0.38, 0.54, 0.7, 0.86]) add('crosswalk', s, 0, 1, 0);
add('cone', 0.31, -78, 1, 0);
add('cone', 0.315, -62, 1, 1);
add('cone', 0.32, -46, 1, 0);

export const HUNGARORING_TRACK: TrackDef = {
  id: 'hungaroring',
  name: 'Hungaroring',
  subtitle: 'Nagydíj · 3 kör',
  description:
    'A magyar nagydíj otthona: hosszú célegyenes, szűk jobbos T1, felkapaszkodó jobb oldal és lassú belső kanyarok. A pálya mellett lelátók, gumifalak és boxutca – a Forma–1 világa.',
  centerline: CENTERLINE,
  roadHalfWidth: 100,
  kerbWidth: 12,
  sidewalkWidth: 34,
  startS: 0.045,
  laps: 3,
  theme: 'circuit',
  props,
  boostPads: [
    { s: 0.13, lateral: 0, length: 250, width: 150 },
    { s: 0.5, lateral: 0, length: 250, width: 150 },
    { s: 0.82, lateral: 0, length: 250, width: 150 },
  ],
  itemRows: [
    { s: 0.1, lateral: 0, count: 4, spacing: 54 },
    { s: 0.33, lateral: 0, count: 4, spacing: 54 },
    { s: 0.6, lateral: 0, count: 4, spacing: 54 },
    { s: 0.88, lateral: 0, count: 4, spacing: 54 },
  ],
  ambient: {
    sky: '#1b2430',
    asphalt: '#454a53',
    asphaltDark: '#383c44',
    lane: '#f5f0e6',
    kerb: '#d23b32',
    sidewalk: '#b8b6ae',
    wall: '#3a7448',
    accent: '#e10600',
    shadow: 'rgba(6,9,12,0.42)',
  },
  cameraZoom: 0.84,
};
